// Sentinel prefixes for the chat turns the STUDIO fires rather than the user.
//
// Two families, same mechanism:
//
//   1. Preview self-check corrections (components/ToolCard.jsx): a render crash,
//      overflow, overlap or spacing finding sent back to the model.
//   2. Modal-fired actions (routes/Canvas.jsx): Change template, Merge slides,
//      Split into two slides.
//
// Both are PLAIN user messages on the wire (there is no third thread role, and
// every server path persists userText unconditionally), so the model reads them
// verbatim and the strings must stay stable. But the transcript is for messages
// between the user and the agent, not for the studio's machinery (Alex,
// 2026-07-30): a client hit "Change template" and could not tell that a six-line
// instruction had been written into the chat on their behalf. So the renderer
// (canvas/Conversation.tsx) matches the prefix and shows ONE quiet line instead.
//
// One table, shared by the emitters and the renderer, so the two can't drift.
//
// Note what the wire carries for the modal-fired actions: a machine-readable id
// plus a detail, NOT the past-tense English label. A finished-sounding header
// inside an instruction ("Changed template to X") is exactly the kind of thing
// the weak runtime model echoes back or reads as already-done. The label is a
// client-side concern and lives here.
type ActionNote = {
  prefix: string;
  /** The transcript's one-line label. Takes the sentinel line's detail; the
   *  self-check entries ignore it, since their prefix runs into prose. */
  label: (detail: string) => string;
};

export const ACTION_NOTES = {
  // --- preview self-checks: existing prose prefixes, fixed labels ---
  render: { prefix: 'PREVIEW RENDER FAILED:', label: () => 'Studio fixed a render error' },
  overflow: { prefix: 'PREVIEW OVERFLOW:', label: () => 'Studio tightened the slide to fit' },
  layout: { prefix: 'PREVIEW LAYOUT CHECK:', label: () => 'Studio adjusted overlapping elements' },
  spacing: { prefix: 'PREVIEW SPACING CHECK:', label: () => 'Studio adjusted spacing' },
  // --- modal-fired actions: id + detail on a leading line of their own ---
  changeTemplate: {
    prefix: 'STUDIO ACTION: change-template ·',
    label: (detail: string) => (detail ? `Changed template → ${detail}` : 'Changed template'),
  },
  mergeSlides: {
    prefix: 'STUDIO ACTION: merge-slides ·',
    label: (detail: string) => (detail ? `Merged ${detail}` : 'Merged slides'),
  },
  splitSlide: {
    prefix: 'STUDIO ACTION: split-slide ·',
    label: (detail: string) => (detail ? `Split ${detail}` : 'Split a slide'),
  },
  // `satisfies` rather than an annotation, so the keys stay literal and
  // `ActionNoteId` below is the real roster rather than `string`.
} satisfies Record<string, ActionNote>;

/** The ids above — every emitter names one of these, so a typo is a compile
 *  error rather than a thrown `Unknown action note id`. */
export type ActionNoteId = keyof typeof ACTION_NOTES;

/** Prefix-only view of the table, for call sites that build their own string
 *  (the four ToolCard corrections, whose prefix is inline prose).
 *
 *  The assertion is what `Object.fromEntries` costs: it always widens keys to
 *  `string`, and this maps over ACTION_NOTES' own entries, so the key set is
 *  the same one by construction. */
export const ACTION_PREFIXES = Object.fromEntries(
  Object.entries(ACTION_NOTES).map(([id, note]) => [id, note.prefix]),
) as Record<ActionNoteId, string>;

/**
 * Build a modal-fired action prompt: the sentinel and its detail on their own
 * line, then a blank line, then the real instruction.
 *
 * A LEADING LINE rather than an inline prefix on purpose: merge and split are
 * numbered lists and split embeds a fenced JSX block, so this leaves the
 * instruction reading exactly as it did before the sentinel existed.
 */
export function actionPrompt(id: ActionNoteId, detail: string, body: string): string {
  const note = ACTION_NOTES[id];
  if (!note) throw new Error(`Unknown action note id: ${id}`);
  return `${note.prefix} ${detail}\n\n${body}`;
}

/**
 * The "make it fit" correction, verbatim, for every surface that offers it:
 * the chat card's Tighten to fit, its automatic rework of a badly overflowing
 * proposal, and the on-slide review bar's Tighten to fit. One string — three
 * places asking the model for the same thing in three different wordings is
 * how a weak model learns three different behaviours.
 *
 * `detail` is measurement data from the preview ("112px tall"), not
 * instructions — the caller caps it before it gets here.
 */
// `previous` is a DISPLAY string ("248px"), not a measurement, and it is
// optional: it goes straight into the sentence beside `detail`, which is also
// prose, and the Canvas caller (a manual "Tighten to fit") has no previous
// round to compare against. Typing it `number` would have read better from the
// name and been wrong at both call sites.
export function overflowFixPrompt(
  detail: string | null | undefined,
  previous?: string | null,
  opts: { keepCopy?: boolean } = {},
): string {
  // Short and ordered, deliberately. This was 309 words of conditional advice —
  // a tutorial on column balancing, maxWidth, when re-flowing counts as an
  // unrequested edit. The OpenCode models don't weigh a wall of prose, they follow
  // the most concrete-sounding instruction in it, and the most concrete one
  // here was "re-flow into two columns". On a slide built from PrincipleRows
  // (already two columns each), that HALVES the body width and doubles the
  // wrapping: a 248px overflow became 599px, and the model said so and kept
  // going (Jake, 2026-08-02).
  //
  // So: fewer words, and no recommended arrangement change. The rule that
  // actually catches the failure — "if it didn't shrink, put it back" — is
  // enforced in code (lib/overflowWatch.js) rather than asked for here, and
  // stated in one line only because a model that knows the number is coming
  // behaves better than one ambushed by it.
  const worse = previous ? ` Your last attempt made it WORSE (${previous} → ${detail}); undo that approach.` : '';
  // `keepCopy` — the author pinned this slide's words (manifest `copyLocked`).
  // The ladder then loses its two lossy rungs entirely rather than merely
  // discouraging them: a weak model asked not to shorten, in a message whose
  // most concrete instruction IS "shorter copy", shortens. What is not in the
  // prompt cannot be followed. The remaining rungs are the ones that fit a
  // slide WITHOUT touching the words, and there is an explicit way out when
  // none of them is enough — an overflowing slide the author can decide about
  // beats a tidy one missing their sentence.
  const ladder = opts.keepCopy
    ? ' Make the SMALLEST change that fits, in this order: 1) a smaller size step,' +
      ' 2) a tighter maxWidth or less spacing.' +
      ' The words are LOCKED: do NOT shorten, reword, re-punctuate or drop any text.' +
      ' If it cannot fit without changing the words, leave it as it is and say so.'
    : ' Make the SMALLEST change that fits, in this order: 1) a smaller size step, 2) shorter copy,' +
      ' 3) drop the least important item.';
  return (
    ACTION_NOTES.overflow.prefix +
    ` the slide does not fit (${detail || 'overflowing'}).${worse}` +
    ladder +
    ' Never enlarge anything, never set text below 1cqw, never empty the slide.' +
    ' Do not change the layout or the number of columns.' +
    ' Every edit must REDUCE the number above — if it does not, restore the previous version and stop.' +
    ' Just fix it; no explanation.'
  );
}

/**
 * If `text` is a studio-fired turn, return the one-line label the transcript
 * should show in place of it; otherwise null (a real message the user typed).
 */
export function actionNote(text: string | null | undefined): string | null {
  const t = (text || '').trimStart();
  for (const note of Object.values(ACTION_NOTES)) {
    if (!t.startsWith(note.prefix)) continue;
    // The detail is the rest of the sentinel's own line. The self-check
    // prefixes run straight into prose, so their labels ignore it.
    // `split` always yields at least one element, but that is not something
    // the checker knows, and `?? ''` says so more cheaply than an assertion.
    const firstLine = (t.slice(note.prefix.length).split('\n')[0] ?? '').trim();
    return note.label(firstLine);
  }
  return null;
}
